import React from 'react';
import {
  AbsoluteFill,
  spring,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
} from 'remotion';

// 多智能体协作可视化效果
export const MultiAgentCollaboration: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  // Agent 定义
  const agents = [
    { id: 0, name: 'Agent A', role: '规划者', x: 300, y: 200, color: '#FF6B6B' },
    { id: 1, name: 'Agent B', role: '执行者', x: 960, y: 200, color: '#4ECDC4' },
    { id: 2, name: 'Agent C', role: '评估者', x: 300, y: 500, color: '#45B7D1' },
    { id: 3, name: 'Agent D', role: '学习者', x: 960, y: 500, color: '#96CEB4' },
  ];

  // 任务节点
  const tasks = [
    { id: 'task-1', name: '任务分解', x: 630, y: 350, status: 'complete' },
    { id: 'task-2', name: '并行执行', x: 630, y: 350, status: 'active' },
    { id: 'task-3', name: '结果融合', x: 630, y: 350, status: 'pending' },
  ];

  // 通信消息（Agent 之间的消息流）
  const messages = [
    { from: 0, to: 1, content: '任务分配', progress: Math.min(1, frame / 60) },
    { from: 0, to: 2, content: '评估请求', progress: Math.min(1, Math.max(0, (frame - 30) / 60)) },
    { from: 1, to: 3, content: '执行数据', progress: Math.min(1, Math.max(0, (frame - 60) / 60)) },
    { from: 2, to: 3, content: '评估反馈', progress: Math.min(1, Math.max(0, (frame - 90) / 60)) },
    { from: 3, to: 0, content: '学习结果', progress: Math.min(1, Math.max(0, (frame - 120) / 60)) },
  ];

  // 数据流粒子（在连接线上移动的粒子）
  const dataParticles = Array.from({ length: 40 }, (_, i) => {
    const seed = i * 50;
    const msgIndex = i % messages.length;
    const msg = messages[msgIndex];
    const fromAgent = agents[msg.from];
    const toAgent = agents[msg.to];
    
    // 计算粒子在线上的位置
    const progress = (frame * 0.02 + seed * 0.1) % 1;
    const x = fromAgent.x + (toAgent.x - fromAgent.x) * progress;
    const y = fromAgent.y + (toAgent.y - fromAgent.y) * progress;
    
    return {
      id: i,
      x,
      y,
      size: 4 + Math.abs(Math.sin(seed)) * 2,
      color: fromAgent.color,
      opacity: 0.6 + Math.abs(Math.cos(frame * 0.1 + seed)) * 0.3,
    };
  });

  // 协作状态面板
  const collaborationState = {
    iteration: Math.floor(frame / 150) + 1,
    messagesExchanged: Math.min(156, frame),
    tasksCompleted: Math.floor(frame / 50),
    efficiency: Math.min(98, 60 + frame * 0.2).toFixed(1),
  };

  return (
    <AbsoluteFill style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
      {/* 标题 */}
      <div
        style={{
          position: 'absolute',
          top: 40,
          left: 0,
          right: 0,
          textAlign: 'center',
          fontSize: 54,
          fontWeight: 'bold',
          color: '#FFFFFF',
          opacity: spring({ frame, fps, config: { damping: 200 } }),
          transform: `translateY(${interpolate(
            spring({ frame, fps, config: { damping: 200 } }),
            [0, 1],
            [-50, 0]
          )}px)`,
          textShadow: '0 2px 10px rgba(0,0,0,0.3)',
        }}
      >
        多智能体协作系统
      </div>

      {/* 中心任务节点 */}
      <div
        style={{
          position: 'absolute',
          left: 580,
          top: 300,
          width: 260,
          height: 100,
          background: 'rgba(255, 255, 255, 0.95)',
          borderRadius: 20,
          boxShadow: '0 10px 40px rgba(0,0,0,0.3)',
          opacity: spring({ frame: frame - 10, fps, config: { damping: 200 } }),
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 20,
        }}
      >
        <div style={{ fontSize: 24, fontWeight: 'bold', color: '#667eea' }}>
          {tasks[Math.floor(frame / 50) % 3].name}
        </div>
        <div style={{ fontSize: 18, color: '#666', marginTop: 8 }}>
          第 {collaborationState.iteration} 轮迭代
        </div>
      </div>

      {/* Agent 节点 */}
      {agents.map((agent, index) => {
        const agentSpring = spring({
          frame: frame - index * 10,
          fps,
          config: { damping: 200 },
        });

        const pulseScale = 1 + Math.sin(frame * 0.05 + index) * 0.05;

        return (
          <div
            key={agent.id}
            style={{
              position: 'absolute',
              left: agent.x - 100,
              top: agent.y - 60,
              width: 200,
              height: 120,
              background: 'rgba(255, 255, 255, 0.95)',
              borderRadius: 20,
              boxShadow: `0 10px 40px rgba(0,0,0,0.3), 0 0 0 ${4 + Math.sin(frame * 0.1) * 2}px ${agent.color}40`,
              opacity: Math.max(0, agentSpring),
              transform: `scale(${Math.max(0.001, agentSpring) * pulseScale})`,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 15,
            }}
          >
            {/* Agent 图标 */}
            <div
              style={{
                width: 50,
                height: 50,
                background: agent.color,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 24,
                fontWeight: 'bold',
                color: 'white',
                marginBottom: 10,
              }}
            >
              {agent.name.split(' ')[1]}
            </div>
            
            <div style={{ fontSize: 20, fontWeight: 'bold', color: '#333' }}>
              {agent.name}
            </div>
            <div style={{ fontSize: 16, color: '#666', marginTop: 4 }}>
              {agent.role}
            </div>
          </div>
        );
      })}

      {/* 连接线（Agent 之间） */}
      <svg
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          pointerEvents: 'none',
        }}
      >
        {/* Agent 到中心的连接线 */}
        {agents.map((agent) => (
          <line
            key={`line-${agent.id}`}
            x1={agent.x}
            y1={agent.y}
            x2={710}
            y2={350}
            stroke={agent.color}
            strokeWidth="3"
            strokeDasharray="10,5"
            opacity={0.4}
          />
        ))}

        {/* Agent 之间的连接线 */}
        {messages.map((msg, index) => {
          const fromAgent = agents[msg.from];
          const toAgent = agents[msg.to];
          const lineProgress = Math.min(1, Math.max(0, (frame - index * 30) / 60));
          
          return (
            <g key={`msg-line-${index}`}>
              <line
                x1={fromAgent.x}
                y1={fromAgent.y}
                x2={toAgent.x}
                y2={toAgent.y}
                stroke={fromAgent.color}
                strokeWidth="2"
                opacity={0.6 * lineProgress}
              />
              {/* 消息标签 */}
              {lineProgress > 0.5 && (
                <text
                  x={(fromAgent.x + toAgent.x) / 2}
                  y={(fromAgent.y + toAgent.y) / 2}
                  fill="white"
                  fontSize="16"
                  textAnchor="middle"
                  opacity={lineProgress}
                >
                  {msg.content}
                </text>
              )}
            </g>
          );
        })}
      </svg>

      {/* 数据流粒子 */}
      {dataParticles.map((particle) => (
        <div
          key={particle.id}
          style={{
            position: 'absolute',
            left: particle.x - particle.size / 2,
            top: particle.y - particle.size / 2,
            width: particle.size,
            height: particle.size,
            background: particle.color,
            borderRadius: '50%',
            opacity: particle.opacity,
            boxShadow: `0 0 ${particle.size * 2}px ${particle.color}`,
          }}
        />
      ))}

      {/* 协作状态面板 */}
      <div
        style={{
          position: 'absolute',
          bottom: 20,
          left: 20,
          background: 'rgba(255, 255, 255, 0.95)',
          borderRadius: 20,
          padding: 20,
          boxShadow: '0 10px 40px rgba(0,0,0,0.3)',
          opacity: spring({ frame: frame - 30, fps, config: { damping: 200 } }),
        }}
      >
        <div style={{ fontSize: 18, fontWeight: 'bold', color: '#667eea', marginBottom: 15 }}>
          📊 协作状态
        </div>
        <div style={{ fontSize: 14, color: '#333', marginBottom: 8 }}>
          迭代次数: {collaborationState.iteration}
        </div>
        <div style={{ fontSize: 14, color: '#333', marginBottom: 8 }}>
          消息交换: {collaborationState.messagesExchanged}
        </div>
        <div style={{ fontSize: 14, color: '#333', marginBottom: 8 }}>
          任务完成: {collaborationState.tasksCompleted}
        </div>
        <div style={{ fontSize: 14, color: '#333' }}>
          协作效率: {collaborationState.efficiency}%
        </div>
      </div>

      {/* 图例 */}
      <div
        style={{
          position: 'absolute',
          bottom: 20,
          right: 20,
          background: 'rgba(255, 255, 255, 0.95)',
          borderRadius: 20,
          padding: 20,
          boxShadow: '0 10px 40px rgba(0,0,0,0.3)',
          opacity: spring({ frame: frame - 40, fps, config: { damping: 200 } }),
        }}
      >
        <div style={{ fontSize: 18, fontWeight: 'bold', color: '#667eea', marginBottom: 15 }}>
          🎯 Agent 角色
        </div>
        {agents.map((agent) => (
          <div key={`legend-${agent.id}`} style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
            <div
              style={{
                width: 20,
                height: 20,
                background: agent.color,
                borderRadius: '50%',
                marginRight: 10,
              }}
            />
            <span style={{ fontSize: 14, color: '#333' }}>
              {agent.role}
            </span>
          </div>
        ))}
      </div>
    </AbsoluteFill>
  );
};
